import React, { useEffect, useState } from 'react';
import {
  DRONE_SPECS,
  STATION_DRONE_CAPACITY_BY_TIER,
  PRIVATE_STATION_TIERS,
  DRONE_LEVEL_XP_THRESHOLDS,
  DRONE_LEVEL_MAX,
  getEquipmentById,
  type DroneType,
} from '@astra/shared';
import { networkManager, type DroneData } from '../../systems/NetworkManager';
import { onGameEvent } from '../../core/GameEvents';
import {
  PALETTE,
  stationContainerStyle,
  StationHeader,
  StationFooter,
  SectionTitle,
  Hint,
} from './stationTheme';

interface DroneBayProps {
  stationId: string;
  onBack: () => void;
}

const TYPES: DroneType[] = ['miner', 'hauler', 'fighter'];

export function DroneBay({ stationId, onBack }: DroneBayProps) {
  const [credits, setCredits] = useState(0);
  const [activeDrones, setActiveDrones] = useState<DroneData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [buying, setBuying] = useState<string | null>(null);
  const [stationTier, setStationTier] = useState(1);
  const [isPrivateStation, setIsPrivateStation] = useState(false);

  // Capital-ship drone bay state
  const [shipBayCapacity, setShipBayCapacity] = useState<number>(0);
  const [shipBayActiveCount, setShipBayActiveCount] = useState<number>(0);
  const [shipBayDroneType, setShipBayDroneType] = useState<'fighter' | 'miner'>('fighter');
  const [shipDronesList, setShipDronesList] = useState<Array<{
    id: string; droneType: string; isDeployed: boolean; isRogue: boolean;
    hull: number; maxHull: number;
    level: number; xp: number; xpToNext: number;
  }>>([]);

  useEffect(() => {
    const unsub = onGameEvent('playerStateUpdate', (s) => setCredits(s.credits));
    return unsub;
  }, []);

  useEffect(() => {
    const refresh = () => {
      const room: any = (networkManager as any).room;
      if (!room?.state) return;
      const station = room.state.stations.get(stationId);
      const privateStation = room.state.privateStations?.get(stationId);
      if (station) {
        setStationTier(station.tier ?? 1);
        setIsPrivateStation(false);
      } else if (privateStation) {
        setStationTier(privateStation.tier ?? 1);
        setIsPrivateStation(true);
      }

      // Station-owned drones in this constellation
      const list: DroneData[] = [];
      for (const [, d] of room.state.drones) {
        if ((d.homeStationId === stationId || d.ownerId === stationId) && !d.isRogue) {
          list.push({
            id: d.id, droneType: d.droneType,
            ownerType: d.ownerType, ownerId: d.ownerId, homeStationId: d.homeStationId,
            x: d.x, y: d.y, velocityX: d.velocityX, velocityY: d.velocityY,
            rotation: d.rotation, hull: d.hull, maxHull: d.maxHull,
            shield: d.shield, maxShield: d.maxShield, state: d.state, isRogue: d.isRogue,
          });
        }
      }
      setActiveDrones(list);

      // Detect equipped drone bay on the local player's ship
      const localSid = networkManager.getLocalSessionId();
      if (localSid) {
        const me = room.state.players.get(localSid);
        if (me?.slots) {
          let bayCap = 0;
          let bayType: 'fighter' | 'miner' = 'fighter';
          me.slots.forEach((s: any) => {
            if (!s.equipped) return;
            const eq: any = getEquipmentById(s.equipped);
            if (!eq) return;
            if (eq.utilityType === 'fighter_drone_bay') {
              bayCap = eq.stats?.droneCapacity ?? 0;
              bayType = 'fighter';
            } else if (eq.utilityType === 'miner_drone_bay') {
              bayCap = eq.stats?.droneCapacity ?? 0;
              bayType = 'miner';
            }
          });
          setShipBayCapacity(bayCap);
          setShipBayDroneType(bayType);
          if (bayCap > 0) networkManager.sendListShipDrones();
        }
      }
    };
    refresh();
    const id = setInterval(refresh, 1500);

    const prev = networkManager.onPurchaseDroneResult;
    networkManager.onPurchaseDroneResult = (data) => {
      setBuying(null);
      if (!data.success && data.error) setError(data.error);
      else setError(null);
      refresh();
    };

    const prevShipBuy = networkManager.onPurchaseShipDroneResult;
    networkManager.onPurchaseShipDroneResult = (data) => {
      if (!data.success && data.error) setError(data.error);
      else setError(null);
      networkManager.sendListShipDrones();
    };

    const prevList = networkManager.onShipDronesList;
    networkManager.onShipDronesList = (data) => {
      setShipDronesList(data.drones);
      setShipBayActiveCount(data.drones.length);
    };

    return () => {
      clearInterval(id);
      networkManager.onPurchaseDroneResult = prev;
      networkManager.onPurchaseShipDroneResult = prevShipBuy;
      networkManager.onShipDronesList = prevList;
    };
  }, [stationId]);

  const capacity = isPrivateStation
    ? (PRIVATE_STATION_TIERS[stationTier as 1|2|3|4]?.droneCapacity ?? 5)
    : (STATION_DRONE_CAPACITY_BY_TIER[stationTier as 1|2|3|4] ?? 3);
  const active = activeDrones.length;
  const canBuy = active < capacity;

  const handleBuy = (type: DroneType) => {
    setBuying(type);
    setError(null);
    networkManager.sendPurchaseDrone(stationId, type);
  };

  return (
    <div style={stationContainerStyle}>
      <StationHeader onBack={onBack}>
        <SectionTitle style={{ fontSize: 11 }}>DRONE BAY</SectionTitle>
        <Hint>
          Capacity: <span style={{ color: canBuy ? PALETTE.GREEN : PALETTE.RED }}>{active} / {capacity}</span>
          {' · '}[ESC] Return to Station
        </Hint>
      </StationHeader>

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {error && <div style={{ color: PALETTE.RED, marginBottom: 12, fontSize: 11 }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {TYPES.map((type) => {
            const spec = DRONE_SPECS[type];
            const affordable = credits >= spec.priceCredits;
            const disabled = !canBuy || !affordable;
            const isBuying = buying === type;
            return (
              <div key={type} style={{ background: PALETTE.PANEL, border: `1px solid ${PALETTE.BORDER}`, borderRadius: 4, padding: 12 }}>
                <div style={{ color: PALETTE.LIGHT, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.05em' }}>{type}</div>
                <div style={{ color: PALETTE.DIM, fontSize: 11, marginBottom: 4 }}>
                  HP {spec.stats.hull} · SH {spec.stats.shield} · SPD {spec.stats.speed}
                </div>
                {type === 'miner' && (
                  <div style={{ color: PALETTE.DIM, fontSize: 11, marginBottom: 4 }}>
                    Cargo {spec.stats.cargoCapacity} · Rate {spec.stats.miningRate?.toFixed(1)}/s
                  </div>
                )}
                {type === 'hauler' && (
                  <div style={{ color: PALETTE.DIM, fontSize: 11, marginBottom: 4 }}>
                    Cargo {spec.stats.cargoCapacity}
                  </div>
                )}
                {type === 'fighter' && (
                  <div style={{ color: PALETTE.DIM, fontSize: 11, marginBottom: 4 }}>
                    DMG {spec.stats.weaponDamage} · RNG {spec.stats.weaponRange}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                  <div style={{ color: affordable ? PALETTE.TEAL : PALETTE.RED, fontSize: 11 }}>
                    {spec.priceCredits.toLocaleString()} CR
                  </div>
                  <button
                    onClick={() => handleBuy(type)}
                    disabled={disabled || isBuying}
                    style={{
                      padding: '6px 10px', fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
                      background: 'transparent',
                      color: (disabled || isBuying) ? PALETTE.DIM : PALETTE.TEAL,
                      border: `1px solid ${(disabled || isBuying) ? PALETTE.DIM : PALETTE.TEAL}`,
                      borderRadius: 4,
                      cursor: (disabled || isBuying) ? 'not-allowed' : 'pointer', fontWeight: 600,
                      letterSpacing: '0.05em',
                    }}
                  >{isBuying ? '...' : 'BUY'}</button>
                </div>
              </div>
            );
          })}
        </div>

        {shipBayCapacity > 0 && (
          <div style={{ marginTop: 24 }}>
            <SectionTitle style={{ fontSize: 11, marginBottom: 4 }}>CAPITAL SHIP BAY</SectionTitle>
            <Hint style={{ marginBottom: 12 }}>
              Bay: <span style={{ color: shipBayActiveCount < shipBayCapacity ? PALETTE.GREEN : PALETTE.RED }}>
                {shipBayActiveCount} / {shipBayCapacity}
              </span> · Drones currently owned by your ship (deployed + in bay)
            </Hint>

            {/* Single purchase card matching the bay type */}
            {(() => {
              const spec = DRONE_SPECS[shipBayDroneType];
              const affordable = credits >= spec.priceCredits;
              const hasRoom = shipBayActiveCount < shipBayCapacity;
              const disabled = !affordable || !hasRoom;
              return (
                <div style={{
                  background: PALETTE.PANEL, border: `1px solid ${PALETTE.BORDER}`,
                  borderRadius: 4, padding: 12, marginBottom: 12,
                }}>
                  <div style={{ color: PALETTE.LIGHT, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>
                    {shipBayDroneType}
                  </div>
                  <div style={{ color: PALETTE.DIM, fontSize: 11, margin: '4px 0' }}>
                    HP {spec.stats.hull} · SH {spec.stats.shield} · SPD {spec.stats.speed}
                    {shipBayDroneType === 'fighter' && ` · DMG ${spec.stats.weaponDamage} · RNG ${spec.stats.weaponRange}`}
                    {shipBayDroneType === 'miner' && ` · Cargo ${spec.stats.cargoCapacity} · Rate ${spec.stats.miningRate?.toFixed(1)}/s`}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                    <span style={{ color: affordable ? PALETTE.TEAL : PALETTE.RED, fontSize: 11 }}>
                      {spec.priceCredits.toLocaleString()} CR
                    </span>
                    <button
                      onClick={() => networkManager.sendPurchaseShipDrone(shipBayDroneType)}
                      disabled={disabled}
                      style={{
                        padding: '6px 14px', fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace",
                        background: 'transparent',
                        color: disabled ? PALETTE.DIM : PALETTE.TEAL,
                        border: `1px solid ${disabled ? PALETTE.DIM : PALETTE.TEAL}`,
                        borderRadius: 4,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        letterSpacing: '0.05em',
                      }}
                    >BUY</button>
                  </div>
                </div>
              );
            })()}

            {/* Owned ship drones */}
            {shipDronesList.length === 0 && (
              <Hint style={{ fontStyle: 'italic' }}>No drones yet. Buy some above.</Hint>
            )}
            {shipDronesList.map((d) => {
              // Compute progress within the current level segment for the XP bar.
              // segmentSize is the XP gap between this level and the next; the
              // bar shows how far through that gap we are. At L4 we render "MAX".
              const atCap = d.level >= DRONE_LEVEL_MAX;
              const segmentStart = DRONE_LEVEL_XP_THRESHOLDS[d.level - 1] ?? 0;
              const segmentEnd = atCap ? d.xp : (DRONE_LEVEL_XP_THRESHOLDS[d.level] ?? d.xp);
              const segmentSize = Math.max(1, segmentEnd - segmentStart);
              const xpIntoSegment = Math.max(0, d.xp - segmentStart);
              const pct = atCap ? 100 : Math.min(100, Math.round((xpIntoSegment / segmentSize) * 100));
              return (
                <div key={d.id} style={{
                  display: 'flex', flexDirection: 'column', gap: 4, padding: 8, marginBottom: 4,
                  background: PALETTE.PANEL, border: `1px solid ${PALETTE.BORDER}`, borderRadius: 4,
                  fontSize: 11, color: PALETTE.LIGHT,
                }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span style={{ color: PALETTE.TEAL, minWidth: 90 }}>
                      {d.droneType.toUpperCase()}
                    </span>
                    <span style={{
                      minWidth: 32, padding: '1px 6px', borderRadius: 3,
                      background: PALETTE.BORDER, color: PALETTE.LIGHT,
                      fontSize: 10, textAlign: 'center', fontWeight: 600,
                    }}>
                      L{d.level}
                    </span>
                    <span style={{ color: PALETTE.DIM, minWidth: 80 }}>
                      HP {Math.round(d.hull)}/{d.maxHull}
                    </span>
                    <span style={{
                      minWidth: 80,
                      color: d.isRogue ? PALETTE.RED : d.isDeployed ? PALETTE.GREEN : PALETTE.DIM,
                    }}>
                      {d.isRogue ? 'ROGUE' : d.isDeployed ? 'DEPLOYED' : 'IN BAY'}
                    </span>
                    {!d.isRogue && (
                      <span style={{ flex: 1, color: PALETTE.DIM, fontSize: 10, fontStyle: 'italic', textAlign: 'right' }}>
                        Use hotkey [B] in flight to deploy/recall
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 10 }}>
                    <div style={{
                      flex: 1, height: 6, background: PALETTE.BORDER, borderRadius: 3, overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${pct}%`, height: '100%',
                        background: atCap ? PALETTE.TEAL : PALETTE.GREEN,
                        transition: 'width 200ms ease-out',
                      }} />
                    </div>
                    <span style={{ color: PALETTE.DIM, minWidth: 110, textAlign: 'right' }}>
                      {atCap ? `XP ${d.xp.toLocaleString()} · MAX` : `XP ${d.xp.toLocaleString()} (${d.xpToNext.toLocaleString()} to L${d.level + 1})`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 24 }}>
          <SectionTitle style={{ fontSize: 11, marginBottom: 8 }}>ACTIVE DRONES</SectionTitle>
          {activeDrones.length === 0 && <Hint>No active drones.</Hint>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {activeDrones.map((d) => (
              <div key={d.id} style={{
                display: 'flex', gap: 12, padding: 8,
                background: PALETTE.PANEL, border: `1px solid ${PALETTE.BORDER}`, borderRadius: 4,
                fontSize: 11, color: PALETTE.LIGHT,
              }}>
                <span style={{ color: PALETTE.TEAL, minWidth: 80 }}>{d.droneType.toUpperCase()}</span>
                <span style={{ minWidth: 100 }}>HP {Math.round(d.hull)}/{d.maxHull}</span>
                <span style={{ color: PALETTE.DIM }}>state: {d.state}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <StationFooter>Purchase and manage station drones</StationFooter>
    </div>
  );
}

export default DroneBay;
